"use client";

import { useState, useCallback, useMemo } from "react";
import type { DomainId } from "@/data/domains";
import * as spatialAudio from "@/lib/audio/spatialAudio";

// =============================================================================
// ZONE STATE MACHINE
// =============================================================================
// Manages the three-state model: idle, peek, read
// Each state has exactly one dominant zone:
//   - idle: Z2 (ring) dominant, Z1/Z3 hug content
//   - peek: Z2 (ring) still dominant but smaller, Z3 rises to 33%vh
//   - read: Z3 (card) dominant, Z1 becomes nav strip, Z2 exits

export type ZoneState = "idle" | "peek" | "read";

// Zone height constants (in pixels unless noted)
export const Z1_HEIGHTS = {
  idle: 192,  // name + 3 subs + padding (doubled for more presence)
  narrow: 130, // compact name + tighter subtitles for narrow phones (< 400px)
  peek: 56,   // name only, tight margin
  read: 52,   // orb nav strip - taller to prevent clipping
} as const;

export const Z3_HEIGHTS = {
  idle: 28,   // caret hint only
  // peek: 33% of vh (calculated dynamically)
  // read: vh - Z1_READ - 8 (calculated dynamically)
} as const;

// Tiny viewport threshold - below this, Z1 collapses to peek-like height
const TINY_VIEWPORT_THRESHOLD = 500;

// Cramped viewport - short heights, or squareish aspects when also relatively short
const CRAMPED_HEIGHT_THRESHOLD = 700; // Below this = always cramped
const CRAMPED_SQUAREISH_HEIGHT = 800; // Squareish + below this = cramped
const CRAMPED_ASPECT_MIN = 0.75;
const CRAMPED_ASPECT_MAX = 1.4;

// Narrow viewport - tall but narrow phones (e.g. Samsung Flip6 at 360×846)
const NARROW_WIDTH_THRESHOLD = 400;

export interface ZoneStateResult {
  // Current state
  zoneState: ZoneState;
  activeOrb: number;

  // Zone heights (px)
  z1Height: number;
  z2Height: number;
  z3Height: number;

  // Transition handlers
  setActiveOrb: (index: number) => void;
  goToIdle: () => void;
  goToPeek: (orbIndex?: number) => void;
  goToRead: () => void;

  // Convenience flags
  isIdle: boolean;
  isPeek: boolean;
  isRead: boolean;
  isTinyViewport: boolean;  // < 500px - hero collapses to name only
  isCrampedViewport: boolean;  // Square-ish or short - hide subtitles
  isNarrowViewport: boolean;  // < 400px wide - compact name, smaller subtitles
}

interface UseZoneStateOptions {
  viewportHeight: number;
  viewportWidth: number;
  initialOrb?: number;
  activeDomain?: DomainId;  // Condense z1 when viewing manifesto
}

export function useZoneState({
  viewportHeight,
  viewportWidth,
  initialOrb = 0,
  activeDomain = "projects",
}: UseZoneStateOptions): ZoneStateResult {
  const [zoneState, setZoneState] = useState<ZoneState>("idle");
  const [activeOrb, setActiveOrb] = useState(initialOrb);

  // Tiny viewport flag - used to collapse hero to name-only
  const isTinyViewport = viewportHeight < TINY_VIEWPORT_THRESHOLD;

  // Cramped viewport - short heights, or squareish aspects when also relatively short
  // Hide subtitles to give ring more room
  const aspectRatio = viewportWidth / viewportHeight;
  const isSquareish = aspectRatio >= CRAMPED_ASPECT_MIN && aspectRatio <= CRAMPED_ASPECT_MAX;
  const isShort = viewportHeight < CRAMPED_HEIGHT_THRESHOLD;
  const isSquareishAndShort = isSquareish && viewportHeight < CRAMPED_SQUAREISH_HEIGHT;
  const isCrampedViewport = isShort || isSquareishAndShort;

  // Narrow viewport - tall but narrow phones (e.g. Flip6 unfolded at 360×846)
  const isNarrowViewport = viewportWidth < NARROW_WIDTH_THRESHOLD;

  // Calculate zone heights based on current state
  const { z1Height, z2Height, z3Height } = useMemo(() => {
    let z1: number;
    let z3: number;

    // Content domains (manifesto, resume, blog) condense z1 to maximize z2 for content
    const isContentDomain = activeDomain === "manifesto" || activeDomain === "resume" || activeDomain === "blog";

    switch (zoneState) {
      case "idle":
        // On tiny/cramped viewports or content domains, collapse Z1 to peek-like height (name only)
        // On narrow viewports, use intermediate height (compact name + tighter subtitles)
        if (isTinyViewport || isCrampedViewport || isContentDomain) {
          z1 = Z1_HEIGHTS.peek;
        } else if (isNarrowViewport) {
          z1 = Z1_HEIGHTS.narrow;
        } else {
          z1 = Z1_HEIGHTS.idle;
        }
        z3 = Z3_HEIGHTS.idle;
        break;
      case "peek":
        z1 = Z1_HEIGHTS.peek;
        z3 = Math.round(viewportHeight * 0.55);
        break;
      case "read":
        z1 = Z1_HEIGHTS.read;
        z3 = viewportHeight - Z1_HEIGHTS.read; // Z3 extends to Z1, no Z2 gap
        break;
    }

    // Z2 is flex remainder (0 in read mode, min 60 otherwise)
    const z2Raw = viewportHeight - z1 - z3;
    const z2 = zoneState === "read" ? Math.max(0, z2Raw) : Math.max(60, z2Raw);

    return { z1Height: z1, z2Height: z2, z3Height: z3 };
  }, [zoneState, viewportHeight, isTinyViewport, isCrampedViewport, isNarrowViewport, activeDomain]);

  // Transition handlers with diegetic audio
  // Philosophy: sounds belong to the world, not generic UI feedback
  // - playHoverLong: orb's pentatonic pitch on selection (idle -> peek)
  // - playDescend: downward sweep entering read (peek -> read)
  // - playAscend: upward sweep returning to ring (read -> peek/idle)

  const goToIdle = useCallback(() => {
    setZoneState((prev) => {
      // Exiting peek - play orb's tone (same as entering)
      if (prev === "peek") {
        spatialAudio.playHoverLong(activeOrb);
      }
      // Ascending back from read - upward sweep + ambient restore + resume blips
      if (prev === "read") {
        spatialAudio.playAscend();
        spatialAudio.setBlipsReadingMode(false);
      }
      return "idle";
    });
  }, [activeOrb]);

  const goToPeek = useCallback((orbIndex?: number) => {
    // Use provided index or current active orb for audio
    const audioOrbIndex = orbIndex !== undefined ? orbIndex : activeOrb;

    if (orbIndex !== undefined) {
      setActiveOrb(orbIndex);
    }
    setZoneState((prev) => {
      if (prev === "idle") {
        // Orb selection - extended hover tone with orb's pitch
        spatialAudio.playHoverLong(audioOrbIndex);
      }
      if (prev === "read") {
        // Ascending back from read - upward sweep + ambient restore + resume blips
        spatialAudio.playAscend();
        spatialAudio.setBlipsReadingMode(false);
      }
      return "peek";
    });
  }, [activeOrb]);

  const goToRead = useCallback(() => {
    setZoneState((prev) => {
      // Descending into read - downward sweep + ambient duck + pause blips
      if (prev !== "read") {
        spatialAudio.playDescend();
        spatialAudio.setBlipsReadingMode(true);
      }
      return "read";
    });
  }, []);

  // Convenience flags
  const isIdle = zoneState === "idle";
  const isPeek = zoneState === "peek";
  const isRead = zoneState === "read";

  return {
    zoneState,
    activeOrb,
    z1Height,
    z2Height,
    z3Height,
    setActiveOrb,
    goToIdle,
    goToPeek,
    goToRead,
    isIdle,
    isPeek,
    isRead,
    isTinyViewport,
    isCrampedViewport,
    isNarrowViewport,
  };
}
